import { PubSub } from '@aws-amplify/pubsub';
import { ElNotification } from 'element-plus';
import { errorMsg } from './message';
import { i18n } from '@/Lang/index';
import { iotconfig } from '@/aws-exports';
import { topicType } from '@/utils/const';
import { thingNamePrefix } from '@/utils/const';
import { retryBackoffWait } from '@/utils';

/**
 * @typedef {object} Result
 * @property {number} status - 実行結果のステータス
 */

let pubsub = null;

const noParamsNotification = {
  title: i18n.global.t('request.error'),
  message: errorMsg.e_error_param,
  type: 'error',
  duration: 2000,
  customClass: 'notify',
};

const mqttFailedNotification = {
  title: i18n.global.t('request.error'),
  message: errorMsg.e_mqtt_failed_err,
  type: 'error',
  duration: 2000,
  customClass: 'notify',
};

/**
 * @param {string} mqttTopic サブスクライブするトピック
 * @param {object} mqttData - MQTT受信時に使用するデータのオブジェクト
 * @param {Function} mqttData.messageHandler - トピックのMQTT受信時に実行する関数
 * @param {string} mqttData.target - ターゲット情報（messageHandlerで使用可能）
 * @param {string} retryCount - リトライ回数（指数関数的リトライで使用）
 * @return {Promise<Result>} 実行結果のオブジェクト
 */
export async function subscribeTopic(mqttTopic, mqttData, retryCount) {
  if (mqttTopic && mqttData.target) {
    try {
      await retryBackoffWait(retryCount);
      const messageHandler = mqttData.messageHandler;
      const sub = pubsub.subscribe({ topics: mqttTopic }).subscribe({
        next: (data) => messageHandler(mqttData.target, data),
        error: (error) => () => {
          throw error;
        },
      });
      console.log('Subscribeded');
      console.log(mqttTopic);
      return new Promise((resolve) => resolve({ status: 0, data: sub }));
    } catch {
      if (!retryCount || retryCount < 3) {
        if (!retryCount) retryCount = 1;
        else retryCount += 1;
        return await subscribeTopic(mqttTopic, mqttData, retryCount);
      } else {
        ElNotification(mqttFailedNotification);
        console.log('Subscribe Failed');
        console.log(mqttTopic);
        // console.log(mqttData);
        // console.log(e);
        return new Promise((resolve) => resolve({ status: -1 }));
      }
    }
  } else {
    ElNotification(noParamsNotification);
    console.log('Subscribe Failed');
    console.log(mqttTopic);
    // console.log(mqttData);
    return new Promise((resolve) => resolve({ status: -2 }));
  }
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {object[]} data.sub - サブスクライブ解除対象のサブスクライブオブジェクトのリスト
 * @param {string} retryCount - リトライ回数（指数関数的リトライで使用）
 * @return {Promise<void>}
 */
export async function unsubscribeTopic(data, retryCount) {
  if (data.sub) {
    try {
      await retryBackoffWait(retryCount);
      for (let sub of data.sub) {
        sub.unsubscribe();
        sub = null;
      }
      // console.log('UnSubscribed');
      // return new Promise(resolve => resolve({ status: 0 }));
    } catch {
      if (!retryCount || retryCount < 3) {
        if (!retryCount) retryCount = 1;
        else retryCount += 1;
        return unsubscribeTopic(data, retryCount);
      }
      // ElNotification(mqttFailedNotification);
      // console.log('UnSubscribed Failed');
      // return new Promise(resolve => resolve({ status: -1 }));
    }
  } else {
    ElNotification(noParamsNotification);
    // console.log('UnSubscribed Failed');
    // return new Promise(resolve => resolve({ status: -2 }));
  }
}

/**
 * @param {string} mqttTopic パブリッシュするトピック
 * @param {object} mqttData - MQTT送信時に使用するデータのオブジェクト
 * @param {object} mqttData.payload - トピックの送信するデータのオブジェクト
 * @param {string} mqttData.target - ターゲット情報
 * @param {string} retryCount - リトライ回数（指数関数的リトライで使用）
 * @return {Promise<Result>} 実行結果のオブジェクト
 */
export async function publishTopic(mqttTopic, mqttData, retryCount) {
  if (mqttTopic && mqttData.target) {
    try {
      await retryBackoffWait(retryCount);
      await pubsub.publish({ topics: mqttTopic, message: mqttData.payload });
      console.log('Published');
      console.log(mqttData.payload.cmd);
      console.log(mqttTopic);
      return new Promise((resolve) => resolve({ status: 0 }));
    } catch {
      if (!retryCount || retryCount < 3) {
        if (!retryCount) retryCount = 1;
        else retryCount += 1;
        return await publishTopic(mqttTopic, mqttData, retryCount);
      } else {
        ElNotification(mqttFailedNotification);
        console.log('Publish Failed');
        console.log(mqttTopic);
        // console.log(mqttData);
        // console.log(e);
        return new Promise((resolve) => resolve({ status: -1 }));
      }
    }
  } else {
    ElNotification(noParamsNotification);
    console.log(mqttTopic);
    // console.log(mqttData);
    return new Promise((resolve) => resolve({ status: -2 }));
  }
}

/**
 * @param {string} type トピックのタイプ
 * @param {object} params - トピックに必要なパラメーターのオブジェクト
 * @param {string} params.tenant - テナントID
 * @param {string} params.target - ターゲットのID（トピックに必要な場合）
 * @return {Promise<string>} トピック
 */
export function getTopic(type, params) {
  if (type === topicType.gatewayResponse) {
    return `tenants/${params.tenant}/devices/${thingNamePrefix}${params.target}/frontend/GatewayCommandResponse`;
  } else if (type === topicType.gatewayRequest) {
    return `tenants/${params.tenant}/devices/${thingNamePrefix}${params.target}/frontend/GatewayCommandRequest`;
  } else if (type === topicType.gatewayStatusChecked) {
    return `tenants/${params.tenant}/areas/${params.target}/frontend/GatewayStatusChecked`;
  } else if (type === topicType.gatewayStatusChange) {
    return `tenants/${params.tenant}/areas/${params.target}/frontend/GatewayStatusChange`;
  } else if (type === topicType.deviceData) {
    return `tenants/${params.tenant}/devices/+/gateway_id/${params.target}/datatypes/+`;
  } else if (type === topicType.areaDeleted) {
    return `tenants/${params.tenant}/frontend/areaDeleted`;
  } else if (type === topicType.userDeleted) {
    return `tenants/${params.tenant}/frontend/userDeleted`;
  } else if (type === topicType.adminChanged) {
    return `tenants/${params.tenant}/frontend/adminChanged`;
  }
  /* 
  else if (type === topicType.keepAlive) {
    return `tenants/${params.tenant}/clients/${params.target}/frontend/keep-alive`;
  } else if (type === topicType.dummy) {
    return `tenants/dummy`;
  } 
  */
}

export async function configurePubSub() {
  console.log('configurePubSub');
  const uuid = crypto.randomUUID();
  const config = iotconfig[import.meta.env.MODE];
  config.clientId = uuid;
  pubsub = new PubSub(config);
  window.localStorage.setItem('uuid', uuid);
}

export function unconfigurePubSub() {
  console.log('unconfigurePubSub');
  pubsub = null;
  window.localStorage.removeItem('uuid');
}

export async function reconfigurePubSub() {
  console.log('reconfigurePubSub');
  unconfigurePubSub();
  await configurePubSub();
}

/**
 * @return {string | null} クライアントID
 */
export function getPubSubClientId() {
  if (pubsub) {
    return pubsub.clientId;
  } else {
    return null;
  }
}
